import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Switch as FilterSwitch,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useQuery } from 'react-query';

import { Member } from 'src/Superadmin/index.types';
import type { PaginatedMembersResponse } from 'src/Superadmin/hooks';
import Row, {
  CountryBranchGroup,
  NO_COUNTRY_GROUP_KEY,
  groupBranchesByCountry,
} from './Row';
import { useAppSelector } from 'src/redux-file/hooks';
import { StyledActionCell, stickyNameCellSx } from './styles';
import SupplierRow from './SupplierRow';
import { StyledPanel } from 'src/Superadmin/index.styles';
import { useBranchesWithCountry } from 'src/utils/branches';
import { client } from 'src/utils/api-client';
import CountryGroupHeader, {
  CountryGroupBulkControls,
} from 'src/Superadmin/components/CountryGroupHeader';
import {
  countryKeysOf,
  useCountryGroupCollapse,
} from 'src/Superadmin/components/CountryGroupHeader/useCountryGroupCollapse';

const MemoizedSupplierRow = React.memo(SupplierRow);
const MemoizedEmployeeRow = React.memo(Row);

// GET /web/settings_v2/organization-members with an additive `countries`
// filter (C07/C10, opt-in per O-04/DECISIONS-RESOLVED.md; widened from a
// single `country` to a repeated `countries` param by
// specs/001-raben-country-masteradmin/mass-specs/specs/
// 20260826-country-multiselect-picker.md). A scoped copy of
// `useOrganizationMembersPaginated` (src/Superadmin/hooks.ts) rather than an
// edit to that shared hook -- it is outside this component's file scope,
// and neither the component spec's nor the task's file list includes it.
// The query key keeps the same 'organization-members-paginated' PREFIX that
// hook uses (react-query v3 invalidateQueries matches by prefix), so the
// existing mutations in ./hooks.ts and the country mutation in ./Row.tsx
// keep refetching this data without any change on their end.
//
// NOTE: this is the file that actually owns the network call the picker
// spec's Interface/Contract table describes for organization-members --
// Users/index.tsx only holds the selected-codes state and passes it down.
// The picker spec's own Affected Files list names Users/index.tsx but not
// this file; touching it is unavoidable to land `countries` on the wire
// (flagged in this component's own report rather than widened silently).
function useCountryScopedMembers(
  page: number,
  pageSize: number,
  isRootMember: boolean,
  countries: string[]
) {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
    is_root_member: isRootMember.toString(),
  });
  // EC-5: an empty selection appends nothing at all -- never an empty
  // `countries` param -- so the legacy no-filter path on the backend is
  // used unchanged.
  countries.forEach((code) => params.append('countries', code));

  const query = useQuery<PaginatedMembersResponse>(
    ['organization-members-paginated', page, pageSize, isRootMember, countries],
    () =>
      client
        .get(`web/settings_v2/organization-members?${params}`)
        .then((res) => res.data)
  );

  return {
    ...query,
    organizationMembers: query.data?.users || [],
    pagination: {
      total: query.data?.total || 0,
      page: query.data?.page || 1,
      pageSize: query.data?.page_size || pageSize,
      hasNext: query.data?.has_next || false,
      hasPrevious: query.data?.has_previous || false,
    },
  };
}

interface Props {
  // Selected country filter, lifted to Users/index.tsx (Goal 3, widened to
  // a list by the country-multiselect-picker) -- [] means no filter
  // (opt-in, O-04: unfiltered behaviour is unchanged).
  countries?: string[];
}

export default function OptimizedUsersTable({ countries = [] }: Props) {
  const { t } = useTranslation();
  const { user } = useAppSelector((state) => state.platform);
  const { branches: allBranches } = useBranchesWithCountry();

  // Pagination state
  const [employeePage, setEmployeePage] = useState(1);
  const [supplierPage, setSupplierPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmployees, setShowEmployees] = useState(true);
  const [showSuppliers, setShowSuppliers] = useState(true);

  const PAGE_SIZE = 50; // Optimal page size for performance

  // Goal 3 narrows rows: a country change can shrink either list, so land
  // back on page 1 rather than risk an out-of-range page.
  useEffect(() => {
    setEmployeePage(1);
    setSupplierPage(1);
  }, [countries]);

  // Fetch employees with pagination
  const {
    organizationMembers: employees,
    pagination: employeePagination,
    isLoading: employeesLoading,
  } = useCountryScopedMembers(
    showEmployees ? employeePage : 1,
    PAGE_SIZE,
    true, // is_root_member = true for employees
    countries
  );

  // Fetch suppliers with pagination
  const {
    organizationMembers: suppliers,
    pagination: supplierPagination,
    isLoading: suppliersLoading,
  } = useCountryScopedMembers(
    showSuppliers ? supplierPage : 1,
    PAGE_SIZE,
    false, // is_root_member = false for suppliers
    countries
  );

  const noCountryLabel = String(
    t('superadmin.settings.tabs.users.usersTable.columns.noCountry')
  );

  // Shared by BOTH tables (Goal 1/3/4): grouped by country, first-seen
  // order, "Bez kraju" trailing. `groupBranchesByCountry`'s own narrowing
  // (Row.tsx, untouched by this component) only ever understood ONE
  // country -- keep that contract exactly as it was rather than widen it:
  // narrow only when the picker resolves to exactly one selected country,
  // otherwise show every group (country-folding, fb029efc2, is what keeps
  // that manageable at 17 countries now, not this narrowing).
  const groups: CountryBranchGroup[] = useMemo(
    () =>
      groupBranchesByCountry(
        allBranches,
        noCountryLabel,
        countries.length === 1 ? countries[0] : undefined
      ),
    [allBranches, noCountryLabel, countries]
  );

  // specs/001-raben-country-masteradmin/mass-specs/specs/
  // 20260826-fe-country-group-folding.md (Goal 1/3/4/5, corrected). ONE
  // collapse-state instance for BOTH the Employees and the Suppliers table
  // below -- Goal 4 requires collapsing Poland in one to collapse it in the
  // other, and since both tables are rendered from this same component with
  // the same `groups`, sharing one hook call (rather than one per table) is
  // what makes that automatic instead of needing its own synchronization.
  //
  // Every group starts collapsed on first visit regardless of scope (Goal
  // 3, corrected -- see StructureTable.tsx's identical comment for why
  // "expand my countries" was wrong). `allBranches` (unfiltered by the
  // country-Select) is used for `allKeys` so collapseAll()/expandAll() act
  // on every country this screen could ever show, not just whatever the
  // Select currently narrows to.
  const allCountryKeys = useMemo(
    () => countryKeysOf(allBranches, NO_COUNTRY_GROUP_KEY),
    [allBranches]
  );
  const storageKey = `superadmin.countryFold.users.${user?.id ?? 'anon'}`;
  const collapse = useCountryGroupCollapse({
    storageKey,
    allKeys: allCountryKeys,
  });

  // EC-6: a screen narrowed to exactly one group (typically the country
  // filter above) renders it expanded, "not collapsible into uselessness"
  // -- see StructureTable.tsx's identical comment for the full reasoning
  // and why this defers to the real per-key state once
  // collapse.isFullyCollapsed is true (an explicit collapseAll() still
  // collapses this sole group too). Applies to BOTH tables below since they
  // share this one `groups` array.
  const resolveOpen = (key: string) =>
    (groups.length === 1 && !collapse.isFullyCollapsed) || collapse.isOpen(key);

  // Filter current user and apply search
  const filteredEmployees = useMemo(() => {
    if (!showEmployees) return [];
    return employees
      .filter((member: Member) => member.id !== user?.id)
      .filter((member: Member) => {
        if (!searchTerm) return true;
        const fullName =
          `${member.first_name} ${member.last_name}`.toLowerCase();
        const email = member.email.toLowerCase();
        const search = searchTerm.toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
  }, [employees, user?.id, searchTerm, showEmployees]);

  const filteredSuppliers = useMemo(() => {
    if (!showSuppliers) return [];
    return suppliers
      .filter((member: Member) => member.id !== user?.id)
      .filter((member: Member) => {
        if (!searchTerm) return true;
        const fullName =
          `${member.first_name} ${member.last_name}`.toLowerCase();
        const email = member.email.toLowerCase();
        const search = searchTerm.toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
  }, [suppliers, user?.id, searchTerm, showSuppliers]);

  // Pagination handlers
  const handleEmployeePageChange = useCallback((_: any, page: number) => {
    setEmployeePage(page);
  }, []);

  const handleSupplierPageChange = useCallback((_: any, page: number) => {
    setSupplierPage(page);
  }, []);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    []
  );

  return (
    <>
      {/* Search and Filters */}
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder={t('superadmin.settings.tabs.users.search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        <FormControlLabel
          control={
            <FilterSwitch
              checked={showEmployees}
              onChange={(e) => setShowEmployees(e.target.checked)}
            />
          }
          label={t('superadmin.settings.tabs.users.usersTable.employees')}
        />
        <FormControlLabel
          control={
            <FilterSwitch
              checked={showSuppliers}
              onChange={(e) => setShowSuppliers(e.target.checked)}
            />
          }
          label={t('superadmin.settings.tabs.users.usersTable.suppliers')}
        />
      </Box>

      {/* What puts an account in each group (the backend's `is_root_member`,
          settings_panel/api_v2.py): any row on the main organisation makes it
          an employee whatever its role; its suppliers come from its branches
          and countries. Everyone else is a supplier's own account. */}
      <Box sx={{ mb: 3 }} data-testid="users-group-help">
        <Typography
          variant="captionText"
          component="p"
          sx={{ color: '#6E6E6E' }}
        >
          <strong>
            {t('superadmin.settings.tabs.users.usersTable.employees')}
          </strong>
          {' – '}
          {t('superadmin.settings.tabs.users.usersTable.groupHelp.employees')}
        </Typography>
        <Typography
          variant="captionText"
          component="p"
          sx={{ color: '#6E6E6E' }}
        >
          <strong>
            {t('superadmin.settings.tabs.users.usersTable.suppliers')}
          </strong>
          {' – '}
          {t('superadmin.settings.tabs.users.usersTable.groupHelp.suppliers')}
        </Typography>
      </Box>

      {/* Goal 4/5: one set of bulk fold controls governs BOTH tables below,
          since they share the one `collapse` instance above. */}
      <Box sx={{ mb: 2 }}>
        <CountryGroupBulkControls
          collapseAllLabel={String(
            t('superadmin.settings.countryFolding.bulk.collapseAll')
          )}
          expandAllLabel={String(
            t('superadmin.settings.countryFolding.bulk.expandAll')
          )}
          onCollapseAll={collapse.collapseAll}
          onExpandAll={collapse.expandAll}
        />
      </Box>

      {/* Employees Table */}
      {showEmployees && (
        <StyledPanel sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {t('superadmin.settings.tabs.users.usersTable.employees')} (
              {employeePagination.total})
            </Typography>
            {employeesLoading && <CircularProgress size={20} />}
          </Box>

          {/* Many countries x branches make the table far wider than the
              panel -- scroll it inside the panel instead of overflowing it. */}
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={stickyNameCellSx}>
                    {t(
                      'superadmin.settings.tabs.users.usersTable.columns.firstNameAndLastName'
                    )}
                  </TableCell>
                  <TableCell rowSpan={2}>
                    {t(
                      'superadmin.settings.tabs.users.usersTable.columns.permissions'
                    )}
                  </TableCell>
                  {/* Goal 1: country group header row, spanning that group's
                    master-switch subcolumn (if any) plus its branch columns
                    -- collapsed groups fold their branch columns down to one
                    (BR-1: the master-switch subcolumn is NEVER part of what
                    collapses). */}
                  {groups.map((group) => {
                    const isOpen = resolveOpen(group.key);
                    const span =
                      (isOpen ? group.branches.length : 1) +
                      (group.countryCode ? 1 : 0);
                    return (
                      <TableCell
                        key={group.key}
                        align="center"
                        colSpan={span}
                        data-testid="country-group-header"
                        sx={{ p: 0 }}
                      >
                        <CountryGroupHeader
                          label={group.label}
                          isOpen={isOpen}
                          onToggle={() => collapse.toggle(group.key)}
                          actionLabel={String(
                            t(
                              isOpen
                                ? 'superadmin.settings.countryFolding.collapseGroup'
                                : 'superadmin.settings.countryFolding.expandGroup',
                              {
                                name: group.label,
                                count: group.branches.length,
                              }
                            )
                          )}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  {groups.map((group) => {
                    const isOpen = resolveOpen(group.key);
                    return (
                      <React.Fragment key={group.key}>
                        {group.countryCode && (
                          <StyledActionCell>
                            {t(
                              'superadmin.settings.tabs.users.usersTable.columns.wholeCountry'
                            )}
                          </StyledActionCell>
                        )}
                        {isOpen ? (
                          group.branches.map((branch) => (
                            <StyledActionCell key={branch.name}>
                              {branch.description}
                            </StyledActionCell>
                          ))
                        ) : (
                          <StyledActionCell
                            key={`${group.key}-collapsed`}
                            sx={{ opacity: 0.7, fontStyle: 'italic' }}
                          >
                            {t(
                              'superadmin.settings.countryFolding.collapsedPlaceholder'
                            )}
                          </StyledActionCell>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map((member: Member) => (
                  <MemoizedEmployeeRow
                    key={member.id}
                    member={member}
                    groups={groups}
                    isGroupOpen={resolveOpen}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>

          {employeePagination.total > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil(employeePagination.total / PAGE_SIZE)}
                page={employeePage}
                onChange={handleEmployeePageChange}
                color="primary"
              />
            </Box>
          )}
        </StyledPanel>
      )}

      {/* Suppliers Table */}
      {showSuppliers && (
        <StyledPanel sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {t('superadmin.settings.tabs.users.usersTable.suppliers')} (
              {supplierPagination.total})
            </Typography>
            {suppliersLoading && <CircularProgress size={20} />}
          </Box>

          {/* Many countries x branches make the table far wider than the
              panel -- scroll it inside the panel instead of overflowing it. */}
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={stickyNameCellSx}>
                    {t(
                      'superadmin.settings.tabs.users.usersTable.columns.firstNameAndLastName'
                    )}
                  </TableCell>
                  <TableCell rowSpan={2}>
                    {t(
                      'superadmin.settings.tabs.users.usersTable.columns.organization'
                    )}
                  </TableCell>
                  <TableCell rowSpan={2}>
                    {t(
                      'superadmin.settings.tabs.users.usersTable.columns.country'
                    )}
                  </TableCell>
                  <TableCell rowSpan={2}>
                    {t(
                      'superadmin.settings.tabs.users.usersTable.columns.branch'
                    )}
                  </TableCell>
                  {/* Goal 3/4: grouped under the same country header row as
                    employees, and narrowed by the same country filter -- both
                    tables render from the SAME `groups` above, and now share
                    the SAME `collapse` instance too, so this stays in
                    lockstep with SupplierRow's switches underneath it AND
                    with the Employees table's fold state (Goal 4). No
                    master-switch subcolumn here (D-03: employees only). */}
                  {groups.map((group) => {
                    const isOpen = resolveOpen(group.key);
                    const span = isOpen ? group.branches.length : 1;
                    return (
                      <TableCell
                        key={group.key}
                        align="center"
                        colSpan={span}
                        data-testid="country-group-header"
                        sx={{ p: 0 }}
                      >
                        <CountryGroupHeader
                          label={group.label}
                          isOpen={isOpen}
                          onToggle={() => collapse.toggle(group.key)}
                          actionLabel={String(
                            t(
                              isOpen
                                ? 'superadmin.settings.countryFolding.collapseGroup'
                                : 'superadmin.settings.countryFolding.expandGroup',
                              {
                                name: group.label,
                                count: group.branches.length,
                              }
                            )
                          )}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
                <TableRow>
                  {groups.map((group) => {
                    const isOpen = resolveOpen(group.key);
                    if (isOpen) {
                      return group.branches.map((branch) => (
                        <StyledActionCell key={branch.name}>
                          {branch.description}
                        </StyledActionCell>
                      ));
                    }
                    return (
                      <StyledActionCell
                        key={group.key}
                        sx={{ opacity: 0.7, fontStyle: 'italic' }}
                      >
                        {t(
                          'superadmin.settings.countryFolding.collapsedPlaceholder'
                        )}
                      </StyledActionCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSuppliers.map((member: Member) => (
                  <MemoizedSupplierRow
                    key={member.id}
                    member={member}
                    groups={groups}
                    isGroupOpen={resolveOpen}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>

          {supplierPagination.total > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil(supplierPagination.total / PAGE_SIZE)}
                page={supplierPage}
                onChange={handleSupplierPageChange}
                color="primary"
              />
            </Box>
          )}
        </StyledPanel>
      )}
    </>
  );
}
